import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { MediFindMoments } from '@medifind/ads';
import { useActiveCount } from '@medifind/hooks';
import { numberWithCommas, useMediaQueryDown } from '@medifind/utils';
import { clearDoctorFilters, selectRouteParams, useRouteStore } from '@medifind/zustand';
import { ConditionFilterStorable } from '../filters';
import { DoctorResultFilterList } from '../filters/DoctorResultFilterList';
import { FilterModal } from '../filters/FilterModal';
import { LocationFilter } from '../filters/LocationFilter';
import SortingHeader from './SortingHeader';
import styles from './FilterGroups.module.scss';

export const generateSearchString = ({ location, radius, specialty }) => {
  let locationString;
  if (radius === 'global' || !radius) {
    locationString = '';
  } else if (radius === 'national') {
    locationString = `in ${location.countryCode === 'US' ? `the ${location.locationString}` : location.locationString}`;
  } else if (!location.city && location.stateProvinceCode && location.countryCode) {
    locationString = `in ${location.stateProvinceCode}, ${location.countryCode}`;
  } else if (location.city && location.stateProvinceCode) {
    locationString = `near ${location.city}, ${location.stateProvinceCode}`;
  } else if (location.city && location.countryCode) {
    locationString = `near ${location.city}, ${location.countryCode}`;
  }
  if (!locationString && !specialty) return 'MediFind suggests these doctors';
  return `${specialty ? specialty + ' ' : ''}Doctors ${locationString}`;
};

const DoctorFiltersGroupV2 = ({
  classes = {},
  style = {},
  onSortByChange,
  sortValue,
  totalResults,
  onLocationChange,
  sortItems,
  smallAt,
  nameAutoComplete,
  projectId,
  modalContainer,
  foundText = 'providers found',
  countryCodesFilter,
  locationPlaceholder,
  showConditionFilter = false,
  conditionFilterProps,
  hideFilterButtonAt = 'md',
  showActiveFilterCount = true,
  rounded = false,
  adOptions,
}) => {
  const activeFiltersCount = useActiveCount();
  const { codingType, specialty } = useRouteStore(selectRouteParams);
  const mediumBreakpoint = useMediaQueryDown('md');
  const isMobileXS = useMediaQueryDown('xs');
  if (totalResults === 1) {
    foundText = foundText.replace(/s /, ' ');
  }

  return (
    <>
      <div className={classNames(classes.filters, styles['doctor-filters-section'], { [styles['rounded']]: rounded })}>
        <div className={classNames(classes.filterWrapper)}>
          {showConditionFilter && (
            <Row className={styles['remove-col-left-right-padding-at-xs']}>
              <Col>
                <FilterModal
                  title="Filter your doctor search"
                  classes={{
                    modalContent: classes.modalContent,
                  }}
                  resultsText={
                    totalResults != null ? `${numberWithCommas(totalResults)} provider${totalResults === 1 ? '' : 's'} found` : null
                  }
                  parentContainer={modalContainer}
                >
                  <ConditionFilterStorable {...conditionFilterProps} classes={classes.conditionFilter} style={style.conditionFilter} />
                </FilterModal>
              </Col>
            </Row>
          )}
          <Row>
            <Col xs={12}>
              <FilterModal
                title="Filter your doctor search"
                classes={{
                  modalContent: classes.modalContent,
                }}
                resultsText={
                  totalResults != null ? `${numberWithCommas(totalResults)} provider${totalResults === 1 ? '' : 's'} found` : null
                }
                parentContainer={modalContainer}
              >
                <LocationFilter
                  label="Location:"
                  showClearButton
                  onLocationSelect={onLocationChange}
                  countryCodesFilter={countryCodesFilter}
                  placeholder={locationPlaceholder}
                  classes={classes.locationFilter}
                  style={{ header: { justifyContent: 'flex-start' }, ...style.locationFilter }}
                />
              </FilterModal>
            </Col>
          </Row>
        </div>
      </div>
      {adOptions?.pageType && (
        <MediFindMoments
          type={'leaderboard'}
          targeting={{
            pageType: adOptions.pageType,
            position: 'top',
          }}
        />
      )}
      <SortingHeader
        classes={classes}
        style={style}
        onSortByChange={onSortByChange}
        sortValue={sortValue}
        sortItems={sortItems}
        totalResults={totalResults}
        foundText={`provider${totalResults === 1 ? '' : 's'} found`}
        clearFilters={clearDoctorFilters}
        hideFilterButtonAt={hideFilterButtonAt}
        showActiveFilterCount={showActiveFilterCount}
        filterList={<DoctorResultFilterList nameAutoComplete={nameAutoComplete} />}
        activeFiltersCount={activeFiltersCount}
      />
    </>
  );
};

DoctorFiltersGroupV2.propTypes = {
  classes: PropTypes.shape({
    filters: PropTypes.string,
    filterWrapper: PropTypes.string,
    filterHeader: PropTypes.string,
    sortContainer: PropTypes.string,
    modalFiltersWrapper: PropTypes.string,
    modalFilterWrapper: PropTypes.string,
    moreFiltersButton: PropTypes.string,
    filterSortContainer: PropTypes.string,
    modalContent: PropTypes.string,
  }),
  onLocationChange: PropTypes.func,
  onSortByChange: PropTypes.func,
  onPageChange: PropTypes.func,
  sortValue: PropTypes.any,
  sortItems: PropTypes.any,
  totalResults: PropTypes.number,
  smallAt: PropTypes.string,
  nameAutoComplete: PropTypes.object,
  pageDetails: PropTypes.object,
  projectId: PropTypes.number,
  ads: PropTypes.bool,
  foundText: PropTypes.string,
  countryCodesFilter: PropTypes.arrayOf(PropTypes.string),
  locationPlaceholder: PropTypes.string,
  showConditionFilter: PropTypes.bool,
  rounded: PropTypes.bool,
  conditionFilterProps: PropTypes.object,
  hideFilterButtonAt: PropTypes.string,
  adOptions: PropTypes.object,
};

export { DoctorFiltersGroupV2 as default, DoctorFiltersGroupV2 };
